// Lightweight Dropbox Saver integration (Drop-ins) that only needs the public App Key.
// This does NOT require app secret, refresh token, or server-side access token.
// It loads the dropins script once, then invokes Dropbox.save so the user can
// pick a destination folder and Dropbox will pull the file from the provided URL.

let dropboxScriptLoaded = false;

const loadDropboxScript = async (appKey: string): Promise<void> => {
  if (dropboxScriptLoaded) return;
  await new Promise<void>((resolve, reject) => {
    const existing = document.getElementById('dropboxjs');
    if (existing) {
      dropboxScriptLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = 'dropboxjs';
    script.type = 'text/javascript';
    script.setAttribute('data-app-key', appKey);
    script.onload = (): void => {
      dropboxScriptLoaded = true;
      resolve();
    };
    script.onerror = (): void => {
      reject(new Error('Failed to load Dropbox Drop-ins script'));
    };
    script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
    document.head.appendChild(script);
  });
};

interface SaveToDropboxOptions {
  onSuccess?: () => void;
  onCancel?: () => void;
  onError?: (error: unknown) => void;
}

// Ensure the filename has an extension; Dropbox saver relies on it to determine file name.
const ensureFilename = (filename: string, fallbackExt: string): string => {
  if (/\.[a-zA-Z0-9]{1,6}$/.test(filename)) return filename;
  return `${filename}.${fallbackExt}`;
};

interface DropboxDropins {
  save: (url: string, filename: string, options: {success?: () => void; cancel?: () => void; error?: (error: unknown) => void}) => void;
}

export const saveFileToDropbox = async (fileUrl: string, filename: string, opts: SaveToDropboxOptions = {}): Promise<void> => {
  try {
    const appKey = (await window.PMW.readLocal('dropbox/getAppKey')) as string;
    if (!appKey) throw new Error('Missing Dropbox App Key');
    await loadDropboxScript(appKey);

    const finalFilename = ensureFilename(filename, 'dat');

    const dropbox = (window as unknown as {Dropbox?: DropboxDropins}).Dropbox;
    if (!dropbox || typeof dropbox.save !== 'function') {
      throw new Error('Dropbox Drop-ins not available after script load');
    }

    dropbox.save(fileUrl, finalFilename, {
      success: () => {
        opts.onSuccess?.();
      },
      cancel: () => {
        opts.onCancel?.();
      },
      error: (err: unknown) => {
        console.error('Dropbox.save error', err);
        opts.onError?.(err);
      },
    });
  } catch (e) {
    console.error(e);
    opts.onError?.(e);
  }
};
